import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocalizedCurrency, LocalizedDate } from '../localization/LocalizedComponents';

interface GiftDetails {
  id: string;
  code: string;
  tier: {
    id: string;
    name: string;
    description: string;
    amount: number;
  };
  months: number;
  message: string | null;
  purchaser: {
    username: string;
    avatar: string | null;
  };
  creatorProfile: {
    id: string;
    displayName: string;
    avatar: string | null;
  };
  purchasedAt: string;
  expiresAt?: string;
  status: string;
  currency: string;
}

export const GiftRedeemPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [gift, setGift] = useState<GiftDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(false);

  const handleLookup = async () => {
    if (!code.trim()) {
      setError('Please enter a gift code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/gifts/preview/${encodeURIComponent(code.trim())}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Gift not found');
      }

      setGift(data.gift);
    } catch (err: any) {
      setError(err.message || 'Failed to lookup gift code');
      setGift(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!gift) return;

    setRedeeming(true);
    setError(null);

    try {
      const response = await fetch('/api/gifts/redeem', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ code: gift.code })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to redeem gift');
      }

      setRedeemed(true);
      
      // Redirect to creator page after 3 seconds
      setTimeout(() => {
        navigate(`/creator/${gift.creatorProfile.id}`);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to redeem gift');
    } finally {
      setRedeeming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleLookup();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-600 rounded-full mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Redeem Gift Membership</h1>
          <p className="text-gray-600">Enter your gift code to unlock your membership</p>
        </div>

        {!gift && !redeemed && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="giftCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Gift Code
                </label>
                <input
                  id="giftCode"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyPress={handleKeyPress}
                  placeholder="GIFT-XXXXXXXXXXXXXXXX"
                  maxLength={25}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-lg font-mono tracking-wider"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Enter the gift code you received (typically starts with "GIFT-")
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              )}

              <button
                onClick={handleLookup}
                disabled={loading || !code.trim()}
                className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Looking up...
                  </span>
                ) : (
                  'Lookup Gift Code'
                )}
              </button>
            </div>
          </div>
        )}

        {gift && !redeemed && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Gift Header */}
            <div className="bg-gradient-to-r from-orange-500 to-pink-600 p-6 text-white">
              <div className="flex items-center mb-4">
                <svg className="w-12 h-12 mr-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 5a3 3 0 015-2.236A3 3 0 0114.83 6H16a2 2 0 110 4h-5V9a1 1 0 10-2 0v1H4a2 2 0 110-4h1.17C5.06 5.687 5 5.35 5 5zm4 1V5a1 1 0 10-1 1h1zm3 0a1 1 0 10-1-1v1h1z" clipRule="evenodd" />
                  <path d="M9 11H3v5a2 2 0 002 2h4v-7zM11 18h4a2 2 0 002-2v-5h-6v7z" />
                </svg>
                <div>
                  <h2 className="text-2xl font-bold">Gift Membership</h2>
                  <p className="text-orange-100">You've received a special gift!</p>
                </div>
              </div>
            </div>

            {/* Gift Details */}
            <div className="p-6 space-y-6">
              {/* Creator Info */}
              <div className="flex items-center space-x-4 pb-6 border-b">
                {gift.creatorProfile.avatar && (
                  <img
                    src={gift.creatorProfile.avatar}
                    alt={gift.creatorProfile.displayName}
                    className="w-16 h-16 rounded-full"
                  />
                )}
                <div>
                  <p className="text-sm text-gray-600">Creator</p>
                  <p className="text-xl font-bold text-gray-900">{gift.creatorProfile.displayName}</p>
                </div>
              </div>

              {/* Tier Info */}
              <div>
                <h3 className="text-sm font-medium text-gray-600 mb-3">Membership Tier</h3>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-lg font-bold text-gray-900">{gift.tier.name}</h4>
                    <span className="text-lg font-bold text-orange-600">
                      <LocalizedCurrency amount={gift.tier.amount} currency={gift.currency} />
                      <span className="text-sm text-gray-600">/mo</span>
                    </span>
                  </div>
                  <p className="text-gray-700">{gift.tier.description}</p>
                </div>
              </div>

              {/* Gift Duration */}
              <div className="flex justify-between items-center py-4 border-t border-b">
                <span className="text-gray-600">Duration:</span>
                <span className="text-lg font-bold text-gray-900">
                  {gift.months} month{gift.months !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Gift Value */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Value:</span>
                <span className="text-2xl font-bold text-orange-600">
                  <LocalizedCurrency amount={gift.tier.amount * gift.months} currency={gift.currency} />
                </span>
              </div>

              {/* Personal Message */}
              {gift.message && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-2">From {gift.purchaser.username}:</p>
                  <p className="text-gray-800 italic">"{gift.message}"</p>
                </div>
              )}

              {/* Gift Status */}
              {gift.status === 'EXPIRED' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Gift Expired</h3>
                    <p className="text-sm text-red-700 mt-1">
                      This gift expired on <LocalizedDate date={gift.expiresAt!} format="long" />
                    </p>
                  </div>
                </div>
              )}

              {gift.status === 'REDEEMED' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start">
                  <svg className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">Already Redeemed</h3>
                    <p className="text-sm text-yellow-700 mt-1">This gift has already been redeemed</p>
                  </div>
                </div>
              )}

              {gift.status === 'CANCELLED' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-start">
                  <svg className="w-5 h-5 text-gray-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-gray-800">Gift Cancelled</h3>
                    <p className="text-sm text-gray-700 mt-1">This gift has been cancelled</p>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="text-sm text-red-700 mt-1">{error}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => {
                    setGift(null);
                    setCode('');
                  }}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Try Different Code
                </button>
                {gift.status === 'ACTIVE' && (
                  <button
                    onClick={handleRedeem}
                    disabled={redeeming}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {redeeming ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Redeeming...
                      </span>
                    ) : (
                      'Redeem Gift'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {redeemed && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Gift Redeemed!</h2>
            <p className="text-gray-600 mb-4">
              Your {gift?.months}-month membership to {gift?.creatorProfile.displayName} is now active
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you to the creator's page...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
